/**
 * SAERP Panel
 *
 * Renders the generated SAE Reconciliation Procedure inline within the
 * Study Documents page. Mirrors the DRCPPanel pattern: status, generate /
 * regenerate / export, stale-generation handling, and a structured inline
 * preview rendering each section as a collapsible card with tables for
 * RACI, data point catalog, discrepancy tiers, milestones, and pre-DBL
 * checklist.
 */

import { GENERATION_LOCK_STALE_MINUTES, GENERATION_LOCK_STALE_MS } from '../../../../functions/src/utils/generationWindow';
import { useState, useEffect } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { httpsCallable } from '../../lib/callable';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { dataManagementDoc } from '../../constants/firestorePaths';
import { useToast } from '../../contexts/ToastContext';
import DownloadButton from '../../features/downloads/DownloadButton';
import { useDocumentDownload } from '../../features/downloads/useDocumentDownload';
import { studyDocumentDownloadOptions } from '../../features/downloads/studyDocumentDownloads';
import {
  Loader2,
  Play,
  RefreshCw,
  FileText,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  ListChecks,
  Lock,
  ClipboardList,
  Users,
  Database,
  Activity,
  BookOpen,
  CheckCircle2,
  EyeOff,
  Tag,
  Layers,
  AlertTriangle,
  GitCompareArrows,
  GraduationCap,
  Library,
} from 'lucide-react';
import { displayGenerationError } from '../../utils/generationFailureText';

/* ---------- Types ---------- */

interface SaerpDoc {
  version?: number;
  status?: string;
  title?: string;
  generatedAt?: any;
  generatedBy?: string;
  studyInfo?: {
    studyTitle?: string;
    shortTitle?: string;
    protocolNumber?: string;
    sponsor?: string;
    phase?: string;
    therapeuticArea?: string;
    edcSystem?: string;
    safetyDatabase?: string;
  };
  generatedContent?: Record<string, any>;
  safetyFormCount?: number;
  safetyVariableCount?: number;
  dataPointCount?: number;
  raciRowCount?: number;
  milestoneCount?: number;
  safetyDatabase?: string | null;
  _generating?: boolean;
  _generationStartedAt?: any;
  _generationStartedBy?: string;
  _generationType?: string;
  _generationError?: string;
  _generationFailedAt?: any;
}

interface SAERPPanelProps {
  projectId: string;
  /**
   * The Hub's in-flight state for this document, so ONE generation disables
   * EVERY control that can start it.
   *
   * This panel is rendered inside the Hub's saerp card, which carries its
   * own Regenerate button, so two controls for the same action sit on screen
   * together. Each used to hold only its own state. Both do read the server's
   * `_generating` marker, but that is set only after a round trip, so at the
   * instant of a click neither could see the other's — and on 2026-08-17 one
   * click on the MCS card produced two requests 0.67ms apart, the second
   * refused by the per-study lock and shown to the user as a failure while the
   * first was succeeding.
   *
   * The Hub owns the authoritative state (its synchronous `generatingDocs`
   * ORed with the same marker), so passing it down closes that window
   * immediately rather than a round trip later.
   */
  generating?: boolean;
}

/* ---------- Section catalog ---------- */

interface SectionSpec {
  id: string;
  title: string;
  icon: React.ReactNode;
  contentKey: string;
}

const SECTIONS: SectionSpec[] = [
  { id: 'documentControl', title: 'Document Control', icon: <Tag className="h-4 w-4" />, contentKey: 'documentControl' },
  { id: 'purposeAndScope', title: 'Purpose and Scope', icon: <BookOpen className="h-4 w-4" />, contentKey: 'purposeAndScope' },
  { id: 'definitions', title: 'Definitions', icon: <Library className="h-4 w-4" />, contentKey: 'definitions' },
  { id: 'regulatoryBasis', title: 'Regulatory and Standards Basis', icon: <ShieldAlert className="h-4 w-4" />, contentKey: 'regulatoryBasis' },
  { id: 'rolesAndResponsibilities', title: 'Roles and Responsibilities', icon: <Users className="h-4 w-4" />, contentKey: 'rolesAndResponsibilities' },
  { id: 'dataSourcesAndSystems', title: 'Data Sources and Systems', icon: <Database className="h-4 w-4" />, contentKey: 'dataSourcesAndSystems' },
  { id: 'reconciliationScope', title: 'Reconciliation Scope (Data Point Catalog)', icon: <Layers className="h-4 w-4" />, contentKey: 'reconciliationScope' },
  { id: 'acceptableDifferences', title: 'Acceptable Differences', icon: <CheckCircle2 className="h-4 w-4" />, contentKey: 'acceptableDifferences' },
  { id: 'frequencyAndMilestones', title: 'Frequency, Triggers, Milestones', icon: <Activity className="h-4 w-4" />, contentKey: 'frequencyAndMilestones' },
  { id: 'discrepancyWorkflow', title: 'Discrepancy Workflow', icon: <AlertTriangle className="h-4 w-4" />, contentKey: 'discrepancyWorkflow' },
  { id: 'blindingProcedures', title: 'Blinding Procedures', icon: <EyeOff className="h-4 w-4" />, contentKey: 'blindingProcedures' },
  { id: 'runMechanics', title: 'Reconciliation Run Mechanics', icon: <GitCompareArrows className="h-4 w-4" />, contentKey: 'runMechanics' },
  { id: 'documentationAndAuditTrail', title: 'Documentation and Audit Trail', icon: <ClipboardList className="h-4 w-4" />, contentKey: 'documentationAndAuditTrail' },
  { id: 'finalReconciliationAndDblSignoff', title: 'Final Reconciliation and DBL Sign-off', icon: <Lock className="h-4 w-4" />, contentKey: 'finalReconciliationAndDblSignoff' },
  { id: 'deviationsAndCapa', title: 'Deviations and CAPA', icon: <ListChecks className="h-4 w-4" />, contentKey: 'deviationsAndCapa' },
  { id: 'trainingRequirements', title: 'Training Requirements', icon: <GraduationCap className="h-4 w-4" />, contentKey: 'trainingRequirements' },
  { id: 'relatedSops', title: 'Related SOPs', icon: <FileText className="h-4 w-4" />, contentKey: 'relatedSops' },
];

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  'in-review': 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
};

const PROGRESS_STEPS = [
  'Loading protocol and study setup...',
  'Reading data dictionary and isolating AE/SAE forms...',
  'Resolving safety database from study setup...',
  'Building reconciliation data point catalog...',
  'Authoring discrepancy tier framework...',
  'Drafting frequency and milestone schedule...',
  'Composing blinding and unblinding procedures...',
  'Defining DBL sign-off criteria...',
  'Assembling final document...',
];

/* ---------- Inline preview helpers ---------- */

function PreviewLabel({ label, value }: { label: string; value: any }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-gray-500 font-medium min-w-[140px]">{label}:</span>
      <span className="text-gray-700">{String(value)}</span>
    </div>
  );
}

function PreviewBulletList({ items }: { items: any[] }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <ul className="list-disc ml-5 space-y-1 text-xs text-gray-700">
      {items.map((it, i) => (
        <li key={i}>{typeof it === 'string' ? it : JSON.stringify(it)}</li>
      ))}
    </ul>
  );
}

function PreviewTable({ headers, rows }: { headers: string[]; rows: any[][] }) {
  if (!rows || rows.length === 0) {
    return <p className="text-xs italic text-gray-400">(no entries)</p>;
  }
  return (
    <div className="overflow-x-auto border border-gray-200 rounded">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="px-2 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, ri) => (
            <tr key={ri} className="hover:bg-gray-50">
              {row.map((cell, ci) => (
                <td key={ci} className="px-2 py-1.5 align-top text-gray-700">
                  {Array.isArray(cell) ? cell.join('; ') : (cell ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderPreview(sectionId: string, content: any): React.ReactNode {
  if (!content) {
    return <p className="text-xs italic text-gray-400">No content generated for this section.</p>;
  }
  switch (sectionId) {
    case 'documentControl':
      return (
        <div className="space-y-2">
          <PreviewLabel label="Title" value={content.title} />
          <PreviewLabel label="Version" value={content.documentVersion} />
          <PreviewLabel label="Effective Date" value={content.effectiveDate} />
          <PreviewLabel label="Supersedes" value={content.supersedesVersion} />
          {Array.isArray(content.approvers) && content.approvers.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mt-2 mb-1">Approvers</p>
              <PreviewTable
                headers={['Role', 'Name', 'Signature Required']}
                rows={content.approvers.map((a: any) => [a.role, a.name, a.signatureRequired ? 'Yes' : 'No'])}
              />
            </div>
          )}
        </div>
      );

    case 'purposeAndScope':
      return (
        <div className="space-y-3">
          {content.purpose && <p className="text-xs text-gray-700">{content.purpose}</p>}
          {Array.isArray(content.scope) && content.scope.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">In Scope</p>
              <PreviewBulletList items={content.scope} />
            </div>
          )}
          {Array.isArray(content.outOfScope) && content.outOfScope.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Out of Scope</p>
              <PreviewBulletList items={content.outOfScope} />
            </div>
          )}
          {content.appliesWhen && <PreviewLabel label="Applies When" value={content.appliesWhen} />}
        </div>
      );

    case 'definitions':
      return (
        <PreviewTable
          headers={['Term', 'Definition']}
          rows={(Array.isArray(content) ? content : []).map((d: any) => [d.term, d.definition])}
        />
      );

    case 'regulatoryBasis':
      return (
        <PreviewTable
          headers={['Source', 'Topic', 'Application Note']}
          rows={(Array.isArray(content) ? content : []).map((r: any) => [r.source, r.topic, r.applicationNote])}
        />
      );

    case 'rolesAndResponsibilities':
      return (
        <div className="space-y-2">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <PreviewTable
            headers={['Role', 'RACI', 'Responsibilities']}
            rows={(content.raci || []).map((r: any) => [
              r.role,
              r.raciCode,
              Array.isArray(r.responsibilities) ? r.responsibilities.join('; ') : r.responsibilities,
            ])}
          />
          {content.accountabilityNote && (
            <p className="text-xs text-gray-600 italic mt-2">{content.accountabilityNote}</p>
          )}
        </div>
      );

    case 'dataSourcesAndSystems':
      return (
        <div className="space-y-3">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          {content.edc && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">EDC</p>
              <div className="space-y-1">
                <PreviewLabel label="System" value={content.edc.system} />
                <PreviewLabel label="Vendor" value={content.edc.vendor} />
                <PreviewLabel label="AE/SAE Forms" value={Array.isArray(content.edc.aeForms) ? content.edc.aeForms.join(', ') : content.edc.aeForms} />
                <PreviewLabel label="Export Approach" value={content.edc.exportApproach} />
              </div>
            </div>
          )}
          {content.safetyDatabase && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Safety Database</p>
              <div className="space-y-1">
                <PreviewLabel label="System" value={content.safetyDatabase.system} />
                <PreviewLabel label="Vendor" value={content.safetyDatabase.vendor} />
                <PreviewLabel label="Export Approach" value={content.safetyDatabase.exportApproach} />
                <PreviewLabel label="Case Blinding" value={content.safetyDatabase.caseBlindingApproach} />
              </div>
            </div>
          )}
          {Array.isArray(content.supportingDocuments) && content.supportingDocuments.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Supporting Documents</p>
              <PreviewBulletList items={content.supportingDocuments} />
            </div>
          )}
        </div>
      );

    case 'reconciliationScope':
      return (
        <div className="space-y-2">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <PreviewTable
            headers={['Field', 'EDC Variable', 'Safety DB Element', 'Criticality', 'Match Rule', 'Rationale']}
            rows={(content.dataPoints || []).map((p: any) => [
              p.field,
              p.edcVariable,
              p.safetyDbElement,
              p.criticality,
              p.matchRule,
              p.rationale,
            ])}
          />
        </div>
      );

    case 'acceptableDifferences':
      return (
        <div className="space-y-2">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <PreviewTable
            headers={['Field', 'Rule', 'Rationale']}
            rows={(content.items || []).map((i: any) => [i.field, i.rule, i.rationale])}
          />
        </div>
      );

    case 'frequencyAndMilestones':
      return (
        <div className="space-y-3">
          {content.routineCadence && <PreviewLabel label="Routine Cadence" value={content.routineCadence} />}
          {Array.isArray(content.adHocTriggers) && content.adHocTriggers.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Ad-Hoc Triggers</p>
              <PreviewBulletList items={content.adHocTriggers} />
            </div>
          )}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Milestones</p>
            <PreviewTable
              headers={['Milestone', 'Timing', 'Scope', 'Owner', 'Applicable When']}
              rows={(content.milestones || []).map((m: any) => [m.milestone, m.typicalTiming, m.scope, m.owner, m.applicableWhen])}
            />
          </div>
          {content.expeditedClocksNote && (
            <PreviewLabel label="Expedited Reporting Clocks" value={content.expeditedClocksNote} />
          )}
        </div>
      );

    case 'discrepancyWorkflow':
      return (
        <div className="space-y-3">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Tiers</p>
            <PreviewTable
              headers={['Tier', 'Definition', 'Examples', 'SLA', 'Owner']}
              rows={(content.tiers || []).map((t: any) => [
                t.tier,
                t.definition,
                Array.isArray(t.examples) ? t.examples.join('; ') : t.examples,
                t.sla,
                t.owner,
              ])}
            />
          </div>
          {Array.isArray(content.resolutionFlow) && content.resolutionFlow.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Resolution Flow</p>
              <ol className="list-decimal ml-5 space-y-1 text-xs text-gray-700">
                {content.resolutionFlow.map((s: any, i: number) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
            </div>
          )}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Escalation</p>
            <PreviewTable
              headers={['Trigger', 'Action', 'Owner']}
              rows={(content.escalation || []).map((e: any) => [e.trigger, e.action, e.owner])}
            />
          </div>
          {content.queryThreadPolicy && (
            <PreviewLabel label="Query Thread Policy" value={content.queryThreadPolicy} />
          )}
        </div>
      );

    case 'blindingProcedures':
      if (content.applicable === false) {
        return <p className="text-xs italic text-gray-500">{content.introduction || 'Not applicable.'}</p>;
      }
      return (
        <div className="space-y-2">
          <PreviewLabel label="Blinding State" value={content.studyBlindingState} />
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          {content.unblindingForSusar && <PreviewLabel label="Unblinding for SUSAR" value={content.unblindingForSusar} />}
          {content.dsmbOnlyUnblinding && <PreviewLabel label="DSMB-Only Unblinding" value={content.dsmbOnlyUnblinding} />}
          {content.investigatorBlindMaintenance && <PreviewLabel label="Investigator Blind" value={content.investigatorBlindMaintenance} />}
          {content.vendorBlindingTooling && <PreviewLabel label="Vendor Tooling" value={content.vendorBlindingTooling} />}
        </div>
      );

    case 'runMechanics':
      return (
        <div className="space-y-3">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <PreviewLabel label="Approach" value={content.approach} />
          {content.approachRationale && <PreviewLabel label="Rationale" value={content.approachRationale} />}
          {Array.isArray(content.extractionSteps) && content.extractionSteps.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Extraction Steps</p>
              <ol className="list-decimal ml-5 space-y-1 text-xs text-gray-700">
                {content.extractionSteps.map((s: any, i: number) => <li key={i}>{s}</li>)}
              </ol>
            </div>
          )}
          {Array.isArray(content.comparisonSteps) && content.comparisonSteps.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Comparison Steps</p>
              <ol className="list-decimal ml-5 space-y-1 text-xs text-gray-700">
                {content.comparisonSteps.map((s: any, i: number) => <li key={i}>{s}</li>)}
              </ol>
            </div>
          )}
          {content.outputFormat && <PreviewLabel label="Output Format" value={content.outputFormat} />}
          {content.validationStatus && <PreviewLabel label="Validation Status" value={content.validationStatus} />}
        </div>
      );

    case 'documentationAndAuditTrail':
      return (
        <div className="space-y-3">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          {content.reconciliationLog && <PreviewLabel label="Reconciliation Log" value={content.reconciliationLog} />}
          {Array.isArray(content.logFields) && content.logFields.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Log Fields</p>
              <PreviewBulletList items={content.logFields} />
            </div>
          )}
          {content.alcoaPlusNote && <PreviewLabel label="ALCOA+ / Part 11" value={content.alcoaPlusNote} />}
          {content.retentionPeriod && <PreviewLabel label="Record Retention" value={content.retentionPeriod} />}
          {content.deviationTracking && <PreviewLabel label="Deviation Tracking" value={content.deviationTracking} />}
        </div>
      );

    case 'finalReconciliationAndDblSignoff':
      return (
        <div className="space-y-3">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Pre-DBL Checklist</p>
            <PreviewTable
              headers={['Item', 'Owner', 'Evidence Required']}
              rows={(content.preDblChecklist || []).map((p: any) => [p.item, p.owner, p.evidenceRequired])}
            />
          </div>
          {content.cutoffPolicy && <PreviewLabel label="Cutoff Policy" value={content.cutoffPolicy} />}
          {content.writtenNotificationPolicy && <PreviewLabel label="Written Notification" value={content.writtenNotificationPolicy} />}
        </div>
      );

    case 'deviationsAndCapa':
      return (
        <div className="space-y-2">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          <PreviewTable
            headers={['Category', 'Root-Cause Examples', 'CAPA Approach']}
            rows={(content.deviationCategories || []).map((c: any) => [
              c.category,
              Array.isArray(c.rootCauseExamples) ? c.rootCauseExamples.join('; ') : c.rootCauseExamples,
              c.capaApproach,
            ])}
          />
          {content.escalation && <PreviewLabel label="Escalation" value={content.escalation} />}
        </div>
      );

    case 'trainingRequirements':
      return (
        <div className="space-y-2">
          {content.introduction && <p className="text-xs text-gray-700">{content.introduction}</p>}
          {Array.isArray(content.rolesRequiringTraining) && content.rolesRequiringTraining.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Roles Requiring Training</p>
              <PreviewBulletList items={content.rolesRequiringTraining} />
            </div>
          )}
          {content.evidenceRequired && <PreviewLabel label="Training Evidence" value={content.evidenceRequired} />}
        </div>
      );

    case 'relatedSops':
      return (
        <PreviewTable
          headers={['SOP', 'Relationship']}
          rows={(Array.isArray(content) ? content : []).map((s: any) => [s.name, s.relationship])}
        />
      );

    default:
      return <pre className="text-xs text-gray-700 whitespace-pre-wrap">{JSON.stringify(content, null, 2)}</pre>;
  }
}

/* ---------- Component ---------- */

export default function SAERPPanel({ projectId, generating: hubGenerating = false }: SAERPPanelProps) {
  const [saerpDoc, setSaerpDoc] = useState<SaerpDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  // Shares its running state with the card's Download button (same group).
  const download = useDocumentDownload(projectId, 'saerp');
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);
  const { showToast } = useToast();

  const STALE_GENERATION_MS = GENERATION_LOCK_STALE_MS;

  const isGeneratingFromMarker = (() => {
    if (!saerpDoc?._generating) return false;
    const startedAt = saerpDoc._generationStartedAt;
    if (!startedAt?.toDate) return true;
    const ageMs = Date.now() - startedAt.toDate().getTime();
    return ageMs < STALE_GENERATION_MS;
  })();

  const isStaleGeneration = (() => {
    if (!saerpDoc?._generating) return false;
    const startedAt = saerpDoc._generationStartedAt;
    if (!startedAt?.toDate) return false;
    return Date.now() - startedAt.toDate().getTime() >= STALE_GENERATION_MS;
  })();

  // Folds in the Hub's in-flight state (the shared guard — see
  // SAERPPanelProps.generating) alongside the local fast path and the Firestore
  // marker, so a click on the card's Regenerate disables this one at once.
  const showGenerating = generating || hubGenerating || (isGeneratingFromMarker && !isStaleGeneration);

  useEffect(() => {
    if (!projectId) return;
    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'saerp'),
      (snapshot) => {
        if (snapshot.exists()) {
          setSaerpDoc(snapshot.data() as SaerpDoc);
        } else {
          setSaerpDoc(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading SAERP:', err);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, [projectId]);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerationProgress(PROGRESS_STEPS[0]);
    let step = 0;
    const progressTimer = setInterval(() => {
      step++;
      if (step < PROGRESS_STEPS.length) {
        setGenerationProgress(PROGRESS_STEPS[step]);
      }
    }, 12000);

    try {
      const generateFn = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_SAE_RECONCILIATION_PROCEDURE_V2, {
        timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE,
      });
      await generateFn({ projectId });
    } catch (err: any) {
      console.error('SAERP generation error:', err);
      showToast(err.message || 'Failed to generate SAE Reconciliation Procedure', 'error');
    } finally {
      clearInterval(progressTimer);
      setGenerating(false);
      setGenerationProgress('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const hasRealContent = !!(saerpDoc && (saerpDoc.version || saerpDoc.title));

  if (!saerpDoc || !hasRealContent) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <p className="text-gray-500 mb-2">
            Generate an SAE Reconciliation Procedure defining how serious adverse events
            captured in the EDC are reconciled against the safety database, with
            field-level comparison scope, discrepancy tier framework, blinding rules,
            and DBL sign-off criteria.
          </p>
          <p className="text-xs text-gray-400 mb-6">
            Content is generated from your protocol, study setup, and AE/SAE forms in the
            data dictionary, grounded in SCDM GCDMP, ICH E2A / E2B(R3) / E6(R3),
            21 CFR 312.32, and CDASH SAE Supplement v2.0.
          </p>

          {saerpDoc?._generationError && !saerpDoc?._generating && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
              <strong>Previous generation failed:</strong> {displayGenerationError(saerpDoc._generationError)}
            </div>
          )}

          {isStaleGeneration && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
              A previous generation appears to have stalled (started more than {GENERATION_LOCK_STALE_MINUTES} minutes ago).
              You can try generating again.
            </div>
          )}

          {showGenerating ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">
                {generating ? 'Generating...' : 'Generation in progress in the background...'}
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-blue-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                {generating ? (generationProgress || 'Generating...') : 'Please wait...'}
              </div>
              <div className="max-w-xs mx-auto">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
                </div>
              </div>
              <p className="text-xs text-gray-400">
                This may take 2-4 minutes for the full document to generate.
              </p>
            </div>
          ) : (
            <button
              onClick={handleGenerate}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium"
            >
              <Play className="h-4 w-4" />
              Generate SAE Reconciliation Procedure
            </button>
          )}
        </div>
      </div>
    );
  }

  const status = saerpDoc.status || 'draft';
  const statusStyle = STATUS_STYLES[status] || STATUS_STYLES.draft;
  const content = saerpDoc.generatedContent || {};

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3 flex-wrap gap-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-gray-800">SAE Reconciliation Procedure</h2>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusStyle}`}>
            {status === 'in-review' ? 'In Review' : status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          <span className="text-xs text-gray-400 ml-2">
            {saerpDoc.dataPointCount ?? 0} data points
            {' · '}
            {saerpDoc.raciRowCount ?? 0} RACI rows
            {' · '}
            {saerpDoc.milestoneCount ?? 0} milestones
            {' · '}
            {saerpDoc.safetyFormCount ?? 0} AE/SAE forms
            {saerpDoc.safetyDatabase && (<>{' · '}{saerpDoc.safetyDatabase}</>)}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <DownloadButton
            download={download}
            options={studyDocumentDownloadOptions('saerp', { projectId })}
            variant="secondary"
            size="sm"
          />
        </div>
      </div>

      {saerpDoc?._generationError && !saerpDoc?._generating && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          <strong>Previous generation failed:</strong> {displayGenerationError(saerpDoc._generationError)}
        </div>
      )}

      {isStaleGeneration && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
          A previous generation appears to have stalled (started more than {GENERATION_LOCK_STALE_MINUTES} minutes ago).
          You can try generating again.
        </div>
      )}

      {showGenerating && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm font-medium text-blue-700 mb-1">
            {generating ? 'Regenerating...' : 'Generation in progress in the background...'}
          </p>
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            {generating ? (generationProgress || 'Generating...') : 'Please wait...'}
          </div>
          <div className="mt-2 max-w-xs">
            <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {SECTIONS.map((section, idx) => {
          const sectionContent = content[section.contentKey];
          const isExpanded = expandedSectionId === section.id;
          return (
            <div key={section.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <button
                onClick={() => setExpandedSectionId(isExpanded ? null : section.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                )}
                <span className="text-xs font-mono text-gray-400 w-8">{idx + 1}</span>
                <span className="text-rose-600">{section.icon}</span>
                <span className="text-sm font-medium text-gray-800 flex-1">{section.title}</span>
                {!sectionContent && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                    empty
                  </span>
                )}
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 ml-11 max-h-[60vh] overflow-y-auto">
                  {renderPreview(section.id, sectionContent)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => {
            if (
              confirm(
                'Regenerate the entire SAE Reconciliation Procedure with the latest study data? Existing content will be overwritten.',
              )
            ) {
              handleGenerate();
            }
          }}
          disabled={showGenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${showGenerating ? 'animate-spin' : ''}`} />
          Regenerate
        </button>
      </div>
    </div>
  );
}
